import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Button, IconButton, InputAdornment, Link, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useStore } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import settings from '/@/core/settings';
import { type AccessRequestDetail, getAccessRequestApi } from '/@/hooks/access-requests';
import { _ } from '/@/locale';
import { login as loginAction } from '/@/store/session/actions';
import { getAppInstance, getUser } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { AppInstance, User } from '/@/types/core';
import { messageForUser } from '/@/utils/errors';
import { reverse } from '/@/utils/router';
import { AccessRequestScreen, BrandLogo } from '/@/views/users/request-access/submitted';

/**
 * Same condition as `LoginForm.shouldHoldFieldviewModalAfterLogin` and
 * `App.shouldHoldFieldviewModalAfterLogin`: a user Identity has not validated must stay
 * on the current screen so the global FieldView blocking modal can take over, instead of
 * being sent into the app. This screen is only reachable while that flag is on, and the
 * population that reaches it -- an admin-accepted request -- is precisely the one
 * Identity had not validated, so the branch is the common case here, not an edge.
 */
const shouldHoldFieldviewModalAfterLogin = (appInstance?: AppInstance, user?: User) =>
    Boolean(user && appInstance?.enableFieldviewBlockingModal && !user.validated_type);

interface FormValues {
    password: string;
}

/**
 * Terminal screen for an accepted access request whose email already has an account:
 * there is no password to create, so the applicant only signs in. Reached from the
 * request form right after an auto-accept, and from the acceptance email link.
 *
 * The email is not editable -- it comes from the request the token resolves to, which is
 * also why the form asks for the password alone.
 */
const ExistingAccountLogin = () => {
    // Always set: the route only matches with a token.
    const { token = '' } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const store = useStore();
    const login = bindActionCreators(loginAction, dispatch);

    const [accessRequest, setAccessRequest] = useState<AccessRequestDetail | null>(null);
    const [loadError, setLoadError] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getAccessRequestApi(token)
            .then((detail) => {
                if (cancelled) return;
                // The account was never created (or was removed) -- password creation is
                // the screen that applies, so hand the token over to it.
                if (!detail.userExists) {
                    navigate(reverse('CreatePassword', { params: { token } }), { replace: true });
                    return;
                }
                setAccessRequest(detail);
            })
            .catch(() => {
                if (!cancelled) {
                    setLoadError(_('Este link ya no es válido. Volvé a solicitar acceso.'));
                }
            });
        return () => {
            cancelled = true;
        };
    }, [token, navigate]);

    const {
        control,
        handleSubmit,
        formState: { isValid, isSubmitting },
    } = useForm<FormValues>({ mode: 'onChange', defaultValues: { password: '' } });

    const onSubmit = async (values: FormValues) => {
        if (!accessRequest) return;
        setError('');
        try {
            await login({ email: accessRequest.email, password: values.password });
            // `login` resolves without the session, so read the user that `startSession`
            // just put in the store rather than waiting for a re-render.
            const state = store.getState();
            if (!shouldHoldFieldviewModalAfterLogin(getAppInstance(state), getUser(state))) {
                navigate(reverse('Home'));
            }
        } catch (loginError) {
            setError(messageForUser(loginError, _('Ha ocurrido un error, por favor reintente.')));
        }
    };

    const handleForgotPassword = () => {
        mpTrackEvent('Forgot Password began', undefined);
        navigate(reverse('PasswordResetRequest'));
    };

    const renderBody = () => {
        if (loadError) {
            return (
                <>
                    <Typography variant='subtitle1' sx={{ color: 'grey.700' }}>
                        {loadError}
                    </Typography>
                    <Stack direction='row' justifyContent='center'>
                        <Button
                            variant='contained'
                            onClick={() => navigate(reverse('RequestAccess'))}
                            sx={{ width: { xs: '100%', md: 'auto' } }}
                        >
                            {_('Solicitá acceso')}
                        </Button>
                    </Stack>
                </>
            );
        }

        if (!accessRequest) {
            return (
                <Typography variant='subtitle1' color='primary.mui.inactive'>
                    {_('Cargando...')}
                </Typography>
            );
        }

        return (
            <>
                <Stack spacing={1}>
                    <Typography variant='h5' color='primary' fontWeight={'bold'}>
                        {_('Ya existe una cuenta con este email')}
                    </Typography>
                    <Typography variant='subtitle1' color='primary.mui.inactive'>
                        {_('Ingresá tu contraseña para entrar.')}
                    </Typography>
                </Stack>

                <form noValidate onSubmit={(e) => e.preventDefault()}>
                    <Controller
                        name='password'
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <TextField
                                fullWidth
                                type={showPassword ? 'text' : 'password'}
                                autoComplete='current-password'
                                placeholder={_('Contraseña')}
                                value={field.value}
                                onChange={field.onChange}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') handleSubmit(onSubmit)();
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end'>
                                            <IconButton
                                                aria-label={
                                                    showPassword ? _('Ocultar contraseña') : _('Mostrar contraseña')
                                                }
                                                onClick={() => setShowPassword((shown) => !shown)}
                                                edge='end'
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                </form>

                <Stack direction='row' justifyContent='center'>
                    <Link
                        component='button'
                        type='button'
                        variant='subtitle2'
                        color='secondary'
                        onClick={handleForgotPassword}
                    >
                        {_('Olvidé mi contraseña')}
                    </Link>
                </Stack>

                {error && (
                    <Typography variant='caption' sx={{ color: 'error.main' }}>
                        {error}
                    </Typography>
                )}

                <Stack direction='row' justifyContent='center'>
                    <Button
                        variant='contained'
                        color='secondary'
                        disabled={!isValid || isSubmitting}
                        onClick={handleSubmit(onSubmit)}
                        sx={{ width: { xs: '100%', md: 'auto' } }}
                    >
                        {isSubmitting ? _('Cargando...') : _('Ingresar a {{appName}}', { appName: settings.app.name })}
                    </Button>
                </Stack>
            </>
        );
    };

    return (
        <AccessRequestScreen onBack={() => navigate(reverse('Login'))}>
            <Stack spacing={3}>
                <BrandLogo sx={{ mx: 'auto !important' }} />
                {renderBody()}
            </Stack>
        </AccessRequestScreen>
    );
};

export default ExistingAccountLogin;
